// Core
import React, { useEffect, useCallback, useState } from 'react';
import { Box, Button, Flex, Grid, Text, Image, useColorMode, useDisclosure, useBreakpointValue } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { useQuery } from 'react-query';

// Assets
import { formatDateWithSuffix, colorUserSubLayout, colorUserSubBg } from 'services/Utils';
import profileSub from 'assets/img/profile-sub.png';
import emptyPlan from 'assets/img/empty-plan.png';
import { apiService } from 'services/ApiService';

// Components
import AttentionModal from 'components/Modals/AttentionModal';
import ErrorCard from 'components/Form/Errors';
import YearlyUpgradeModal from './YearlyUpgradeModal';

const PlanState = ({ userData }) => {
	return (
		<Box zIndex="3" position="relative" mt="8px">
	    { userData?.activePlan &&
	    	<Text variant="title-lg" fontSize="24px" fontStyle="normal" fontWeight="700" color="white">
			    ${userData?.activePlan?.price / 100} <span style={{fontSize: '17px', marginLeft: '-5px'}}>/{userData?.activePlan?.paymentType === 'monthly' ? 'month' : 'year' }</span>
				</Text>
	    }

			<Text variant="title-lg" fontSize="15px" color="white">{ userData?.activePlan?.canceledAt === null ? 'Next payment on' : 'Subscription Canceled'} </Text>
			{ userData?.activePlan?.canceledAt === null ? (
			    <Text variant="title-lg" fontSize="15px" color="white">{formatDateWithSuffix(userData?.activePlan?.nextBillingDate)} for ${userData?.activePlan?.price / 100}</Text>
			) : (
			    <Text variant="title-lg" fontSize="15px" color="white">Your plan features remain available until {formatDateWithSuffix(userData?.activePlan?.nextBillingDate)}</Text>
			)}
		</Box>
	);
};

const ActivePlan = (props) => { 
  const { userData, handleCancelMembership, goToCustomerPortal, isLoadingCustomerPortal } = props;

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const { isOpen: isOpenAttention, onClose: onCloseAttention, onOpen: onOpenAttention } = useDisclosure();
  const { isOpen: isOpenYearly, onClose: onCloseYearly, onOpen: onOpenYearly } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const isFuelPlan = userData?.activePlan?.type === 'fuel';
  const handleChangePlan = isFuelPlan ? onOpenYearly : goToCustomerPortal;
  const { data: allPlans } = useQuery({
      queryKey: ['all-plans'],
      queryFn: async () => {
          const response = await apiService.getSubscriptions();
          return response.data;
      },
      refetchOnWindowFocus: false
  });

  const [errorCard, setErrorCard] = useState();
	const [changeStatusSubItem, setChangeStatusSubItem] = useState();
	const [isLoadingChangeStatusSub, setIsLoadingChangeStatusSub] = useState(false);
	const { isOpen, onClose, onOpen } = useDisclosure();
	const changeStatusSub = async () => {
	  setIsLoadingChangeStatusSub(true);

	  if(changeStatusSubItem.status !== 'active' 
	  	&& changeStatusSubItem.status !== 'pause'){
	  	return;
	  }  
	  
	  const data = {
	  	id: changeStatusSubItem.subscriptionId,
	  	status: changeStatusSubItem.status === 'active' ? 'pause' : 'active'
	  };

	  try {
	  	const response = await apiService.changeStatusSubscription(data);

	  	if(response.statusCode !== 200){
				setErrorCard(response.message);
				setIsLoadingChangeStatusSub(false);
				return;
			}

	  	window.location.reload();
	  } catch (e){
	  	setErrorCard(true);
	  }
	  
	  setIsLoadingChangeStatusSub(false);
	};

  return (
	<>
		{ errorCard && ( <ErrorCard error={errorCard} /> )}

		<Box p="16px" borderRadius="16px 16px 0 0" position="relative" overflow="hidden">
			<Image 
				zIndex="2"
				opacity="0.2"
				src={userData?.activePlan?.image || emptyPlan} alt="Profile" 
				right="-10%" top="-40%" objectFit="cover" position="absolute" w="400px" h="400px"/>

			<Box position="absolute" top="0" left="0"
				zIndex="1"
				bg={() => colorUserSubLayout(userData?.activePlan?.type)}
				w="100%" h="100%"/>

			<Flex alignItems="start" zIndex="1" position="relative" zIndex="3">
				<Grid gap="12px" zIndex="1" position="relative" zIndex="3" w="100%" alignItems="center" templateColumns={{ base: '44px 1fr', md: '64px 1fr' }}>
					<Box
					  p={{base: '4px', sm: '10px'}}
					  bg={() => colorUserSubBg(userData?.activePlan?.type)}
					  boxShadow="0px 2.56px 9.344px 0px rgba(0, 0, 0, 0.25)"
					  borderRadius="12px">
					  <Image 
					  	maxW="100%"
					  	src={userData?.activePlan?.image || emptyPlan} 
					  	alt="Plan" />
					</Box>

					<Text variant="title-lg" mt="-2px" 
						fontSize={{base: '16px', sm: '18px', md: '24px' }}
						maxW={{base: '100%', lg: '70%'}}
						fontStyle="normal" fontWeight="800" color="white">
					    {userData?.activePlan?.name}
					</Text>
				</Grid>

				{ !isMobile && userData?.activePlan && userData?.activePlan?.canceledAt === null && (
					<Button 
						w={{base: '100%', md: '80px'}}
						variant="ghost" 
						_hover={{
							background: 'unset'
						}}
						_active={{
							background: 'unset'
						}}
						_focus={{
							boxShadow: 'unset'
						}}
						color={isDark ? 'initial' : 'white'}
						onClick={onOpenAttention}>Cancel</Button>
				)}
			</Flex>

			<Grid gap="24px" alignItems="end" w="100%" templateColumns={{ sm: '1fr', md: '1fr 1fr' }} mt={{base: '0px'}}>	
				<PlanState userData={userData} />			

				{ userData?.activePlan && (
					<Grid gap="8px" alignItems="center" w="100%" templateColumns={{ sm: '1fr', md: '1fr 1fr' }}>	
						{ userData?.activeSubscription?.status === 'paused' ?
							<Button
								onClick={() => {
									setChangeStatusSubItem(userData?.activeSubscription);
									onOpen();
								}}
								zIndex="9" variant="secondary-md">
								Resume
							</Button>
							: <Box></Box>
						}

						<Button
							variant="secondary-md"
							onClick={handleChangePlan}
	       			isLoading={!isFuelPlan && isLoadingCustomerPortal}
	       			zIndex="5"
							>Change Plan</Button>

						{ isMobile && userData?.activePlan?.canceledAt === null &&
							<Button 
								w={{base: '100%'}}
								variant="ghost" 
								zIndex="5"
								_hover={{
									background: 'unset'
								}}
								_active={{
									background: 'unset'
								}}
								_focus={{
									boxShadow: 'unset'
								}}
								color={isDark ? 'initial' : 'white'}
								onClick={onOpenAttention}>Cancel</Button>
						}
					</Grid>
				)}
			</Grid>
		</Box>

		<AttentionModal
		  handle={handleCancelMembership}
		  isOpen={isOpenAttention}
		  onClose={onCloseAttention}
		  title="Cancel Subscription?"
		  description="Are you sure you want to cancel? Some features will no longer be available."
		/>

		<AttentionModal
      handle={changeStatusSub}
      isOpen={isOpen}
      onClose={onClose}
      title={ changeStatusSubItem?.status === 'active' 
      	?	'Pause Membership'
      	: 'Resume Membership'
    	}
      description={ changeStatusSubItem?.status === 'active'
      	? 'Your membership will be paused for 30 days. You won’t be charged or receive entries during this time. You can resume anytime, or it will automatically restart after 30 days with all your details saved.'
      	: 'You are resuming your subscription.'
      }
      />

		{ isFuelPlan &&
			<YearlyUpgradeModal
				isOpen={isOpenYearly}
				onClose={onCloseYearly}
				userData={userData} />
		}
	</>
  );
};

export default ActivePlan;